import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg, fmtDate, peso } from "@/lib/api";
import { PageHeader, Panel, StatusBadge, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ClassificationPicker } from "@/components/registration";
import { toast } from "sonner";

export function MyApplications() {
  const nav = useNavigate();
  const [apps, setApps] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [open, setOpen] = useState(false);
  const [cls, setCls] = useState("");
  useEffect(() => {
    api.get("/registration/applications/mine").then((r) => setApps(r.data));
    api.get("/registration/schemas").then((r) => setSchemas(r.data));
    const pre = sessionStorage.getItem("reg_classification");
    if (pre) { setCls(pre); setOpen(true); }
  }, []);
  const create = async () => {
    try {
      const { data } = await api.post("/registration/applications", { classification: cls });
      sessionStorage.removeItem("reg_classification");
      nav(`/app/my-applications/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Registration" title="My applications" description="Registering an account does not by itself approve a new electrical service connection."
        actions={<Button onClick={() => setOpen(true)} data-testid="new-application-button">New application</Button>} />
      {apps.length === 0 ? <Empty>No applications yet.</Empty> : (
        <div className="grid gap-3">
          {apps.map((a) => (
            <Link key={a.id} to={`/app/my-applications/${a.id}`} data-testid={`my-app-${a.id}`}
              className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-4 hover:border-amber-400 transition-colors">
              <div>
                <p className="font-mono text-sm text-amber-800">{a.reference_no}</p>
                <p className="font-semibold capitalize">{a.classification} · {(a.data.application_type || "").replace("_", " ") || "not started"}</p>
                <p className="text-xs text-slate-500">Updated {fmtDate(a.updated_at)}</p>
              </div>
              <StatusBadge status={a.status} />
            </Link>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-application-dialog">
          <DialogHeader><DialogTitle>Select the account type you are applying for</DialogTitle>
            <DialogDescription>The type decides which fields you fill in. It does not set your tax treatment.</DialogDescription></DialogHeader>
          <ClassificationPicker schemas={schemas} value={cls} onChange={setCls} />
          <Button disabled={!cls} onClick={create} data-testid="create-application-button">Start application</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function MyAccounts() {
  const [accts, setAccts] = useState(null);
  useEffect(() => { api.get("/me/accounts").then((r) => setAccts(r.data)); }, []);
  if (!accts) return null;
  return (
    <div>
      <PageHeader eyebrow="Consumer" title="My accounts" description="Only accounts explicitly linked to you are shown." />
      {accts.length === 0 ? <Empty>No linked accounts yet. Submit a registration for an existing account to link it.</Empty> : accts.map((a) => (
        <Panel key={a.id} className="mb-4" testId={`my-account-${a.account_number}`}
          title={<span><span className="font-mono text-amber-800 mr-2">{a.account_number}</span>{a.name}</span>} description={a.service_address}>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-slate-500"><th className="py-2">Bill</th><th>Period</th><th>Due</th><th className="text-right">Amount</th><th className="text-right">Status</th></tr></thead>
            <tbody>{a.bills.map((b) => (
              <tr key={b.id} className="border-t border-slate-100"><td className="py-2 font-mono text-xs">{b.bill_no}</td><td>{b.period}</td><td>{b.due_date}</td>
                <td className="text-right font-mono">{peso(b.amount_due)}</td><td className="text-right"><StatusBadge status={b.status} /></td></tr>
            ))}</tbody>
          </table>
        </Panel>
      ))}
    </div>
  );
}
